import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../api/client';
import { PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_PAGE, BG_CARD } from '../components/legacy-ball/table-management/designTokens';

interface UpgradeRequest {
  id: string;
  userId: string;
  memberId: string;
  memberEmail: string;
  memberName: string;
  chapterName: string;
  optInNotifications: boolean;
  status: 'Pending' | 'Approved' | 'Rejected';
  reviewedById: string | null;
  reviewedAt: string | null;
  rejectionReason: string | null;
  createdAt: string;
  updatedAt: string;
}

type StatusFilter = 'all' | 'Pending' | 'Approved' | 'Rejected';

export function EmailUpgradeRequestsPage() {
  const [requests, setRequests] = useState<UpgradeRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('Pending');
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>('desc');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [elevateToDelegate, setElevateToDelegate] = useState(true);

  const fetchRequests = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      params.set('sortBy', 'createdAt');
      params.set('sortOrder', sortOrder);
      const data = await apiClient<UpgradeRequest[]>(`/email-upgrade/admin/requests?${params.toString()}`);
      setRequests(data);
    } catch {
      setError('Failed to load upgrade requests.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sortOrder]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const selected = requests.find((r) => r.id === selectedId) || null;

  const handleApprove = async () => {
    if (!selected) return;
    setReviewLoading(true);
    try {
      await apiClient(`/email-upgrade/admin/requests/${selected.id}/review`, {
        method: 'PATCH',
        body: { action: 'approve', elevateToDelegate },
      });
      await fetchRequests();
      setSelectedId(null);
    } catch (err: any) {
      alert(err?.message || 'Failed to approve request.');
    } finally {
      setReviewLoading(false);
    }
  };

  const handleReject = async () => {
    if (!selected) return;
    setReviewLoading(true);
    try {
      await apiClient(`/email-upgrade/admin/requests/${selected.id}/review`, {
        method: 'PATCH',
        body: { action: 'reject', rejectionReason: rejectionReason || undefined },
      });
      await fetchRequests();
      setSelectedId(null);
      setShowRejectForm(false);
      setRejectionReason('');
    } catch (err: any) {
      alert(err?.message || 'Failed to reject request.');
    } finally {
      setReviewLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  };

  const statusBadge = (status: string) => {
    const colors: Record<string, { bg: string; text: string }> = {
      Pending: { bg: '#fef3c7', text: '#92400e' },
      Approved: { bg: '#dcfce7', text: '#166534' },
      Rejected: { bg: '#fee2e2', text: '#991b1b' },
    };
    const c = colors[status] || { bg: '#f3f4f6', text: '#6b7280' };
    return (
      <span style={{ display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '10px', fontSize: '0.72rem', fontWeight: 600, backgroundColor: c.bg, color: c.text }}>
        {status}
      </span>
    );
  };

  return (
    <div style={styles.page}>
      <div style={styles.headerRow}>
        <h1 style={styles.pageTitle}>✉️ Email Login Upgrade Requests</h1>
        <div style={styles.controls}>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value as StatusFilter); setSelectedId(null); }}
            style={styles.select}
            aria-label="Filter by status"
          >
            <option value="all">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            style={styles.sortBtn}
            aria-label={`Sort by date ${sortOrder === 'desc' ? 'oldest first' : 'newest first'}`}
          >
            Date {sortOrder === 'desc' ? '↓' : '↑'}
          </button>
        </div>
      </div>

      {error && <div style={styles.errorBox}>{error}</div>}

      <div style={styles.splitLayout}>
        {/* Left Pane — Request List */}
        <div style={styles.leftPane}>
          {loading ? (
            <div style={styles.emptyState}>Loading...</div>
          ) : requests.length === 0 ? (
            <div style={styles.emptyState}>No requests found.</div>
          ) : (
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Member Name</th>
                  <th style={styles.th}>Request Date</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((r) => (
                  <tr
                    key={r.id}
                    onClick={() => { setSelectedId(r.id); setShowRejectForm(false); setRejectionReason(''); setElevateToDelegate(true); }}
                    style={{
                      ...styles.tr,
                      backgroundColor: selectedId === r.id ? '#eff6ff' : undefined,
                      cursor: 'pointer',
                    }}
                  >
                    <td style={styles.td}>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: TEXT_DARK }}>{r.memberName}</div>
                      <div style={{ fontSize: '0.72rem', color: TEXT_MUTED }}>{r.memberEmail}</div>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontSize: '0.8rem', color: TEXT_DARK }}>{formatDate(r.createdAt)}</span>
                    </td>
                    <td style={styles.td}>{statusBadge(r.status)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Right Pane — Request Details */}
        <div style={styles.rightPane}>
          {!selected ? (
            <div style={styles.emptyDetail}>
              <p style={{ color: TEXT_MUTED, fontSize: '0.9rem' }}>Select a request to view details</p>
            </div>
          ) : (
            <div>
              <h3 style={styles.detailTitle}>{selected.memberName}</h3>
              <div style={styles.detailGrid}>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email</span>
                  <span style={styles.detailValue}>{selected.memberEmail}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Chapter</span>
                  <span style={styles.detailValue}>{selected.chapterName}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Submitted</span>
                  <span style={styles.detailValue}>{formatDateTime(selected.createdAt)}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Status</span>
                  <span style={styles.detailValue}>{statusBadge(selected.status)}</span>
                </div>
                <div style={styles.detailItem}>
                  <span style={styles.detailLabel}>Email Notifications</span>
                  <span style={styles.detailValue}>{selected.optInNotifications ? '✅ Opted In' : '❌ Opted Out'}</span>
                </div>
                {selected.reviewedAt && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Reviewed At</span>
                    <span style={styles.detailValue}>{formatDateTime(selected.reviewedAt)}</span>
                  </div>
                )}
                {selected.rejectionReason && (
                  <div style={styles.detailItem}>
                    <span style={styles.detailLabel}>Rejection Reason</span>
                    <span style={styles.detailValue}>{selected.rejectionReason}</span>
                  </div>
                )}
              </div>

              <div style={styles.infoNote}>
                <strong>What happens on approval:</strong> The user's login will switch from their ticket number to{' '}
                <strong>{selected.memberEmail}</strong>. Their existing password will remain unchanged.
              </div>

              {selected.status === 'Pending' && (
                <div style={styles.actionSection}>
                  {!showRejectForm ? (
                    <>
                      <label style={{ display: 'flex', alignItems: 'center', marginBottom: '0.75rem', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={elevateToDelegate}
                          onChange={(e) => setElevateToDelegate(e.target.checked)}
                          style={{ marginRight: '8px' }}
                        />
                        <span style={{ fontSize: '0.82rem', color: TEXT_DARK }}>
                          Elevate role to Delegate (gives access to organizer tools)
                        </span>
                      </label>
                      <div style={styles.actionBtns}>
                      <button
                        onClick={handleApprove}
                        disabled={reviewLoading}
                        style={styles.approveBtn}
                        aria-label={`Approve request from ${selected.memberName}`}
                      >
                        {reviewLoading ? 'Processing...' : '✅ Approve'}
                      </button>
                      <button
                        onClick={() => setShowRejectForm(true)}
                        disabled={reviewLoading}
                        style={styles.rejectBtn}
                        aria-label={`Reject request from ${selected.memberName}`}
                      >
                        ❌ Reject
                      </button>
                    </div>
                    </>
                  ) : (
                    <div style={styles.rejectForm}>
                      <label style={{ fontSize: '0.8rem', fontWeight: 600, color: TEXT_DARK, display: 'block', marginBottom: '0.4rem' }}>
                        Rejection Reason (optional)
                      </label>
                      <textarea
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Provide a reason for the rejection..."
                        style={styles.textarea}
                        rows={3}
                      />
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                        <button onClick={handleReject} disabled={reviewLoading} style={styles.rejectConfirmBtn}>
                          {reviewLoading ? 'Rejecting...' : 'Confirm Rejection'}
                        </button>
                        <button onClick={() => { setShowRejectForm(false); setRejectionReason(''); }} style={styles.cancelBtn}>
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: {
    padding: '1.5rem',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  pageTitle: {
    margin: 0,
    fontSize: '1.25rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  controls: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  select: {
    padding: '0.4rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.82rem',
    color: TEXT_DARK,
    backgroundColor: BG_CARD,
  },
  sortBtn: {
    padding: '0.4rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.82rem',
    color: TEXT_DARK,
    backgroundColor: BG_CARD,
    cursor: 'pointer',
  },
  errorBox: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#991b1b',
    marginBottom: '1rem',
    fontSize: '0.85rem',
  },
  splitLayout: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1.25rem',
    alignItems: 'start',
  },
  leftPane: {
    backgroundColor: BG_CARD,
    borderRadius: '10px',
    border: `1px solid ${BORDER_LIGHT}`,
    overflow: 'hidden',
    minHeight: '400px',
  },
  rightPane: {
    backgroundColor: BG_CARD,
    borderRadius: '10px',
    border: `1px solid ${BORDER_LIGHT}`,
    padding: '1.25rem',
    minHeight: '400px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
  },
  th: {
    textAlign: 'left' as const,
    padding: '0.6rem 0.75rem',
    fontSize: '0.72rem',
    fontWeight: 700,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
    borderBottom: `2px solid ${BORDER_LIGHT}`,
    backgroundColor: BG_PAGE,
  },
  tr: {
    transition: 'background-color 0.1s',
  },
  td: {
    padding: '0.6rem 0.75rem',
    borderBottom: `1px solid ${BORDER_LIGHT}`,
    verticalAlign: 'middle' as const,
  },
  emptyState: {
    padding: '2rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  emptyDetail: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    minHeight: '300px',
  },
  detailTitle: {
    margin: '0 0 1rem',
    fontSize: '1.1rem',
    fontWeight: 700,
    color: TEXT_DARK,
  },
  detailGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  detailItem: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.2rem',
  },
  detailLabel: {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
  detailValue: {
    fontSize: '0.85rem',
    color: TEXT_DARK,
    fontWeight: 500,
  },
  infoNote: {
    backgroundColor: '#f0f9ff',
    border: '1px solid #bae6fd',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    fontSize: '0.8rem',
    color: '#0c4a6e',
    marginBottom: '1.25rem',
    lineHeight: 1.5,
  },
  actionSection: {
    borderTop: `1px solid ${BORDER_LIGHT}`,
    paddingTop: '1rem',
  },
  actionBtns: {
    display: 'flex',
    gap: '0.75rem',
  },
  approveBtn: {
    padding: '0.55rem 1.25rem',
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  rejectBtn: {
    padding: '0.55rem 1.25rem',
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  rejectForm: {
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '1rem',
  },
  textarea: {
    width: '100%',
    padding: '0.5rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    fontSize: '0.82rem',
    resize: 'vertical' as const,
    fontFamily: 'inherit',
  },
  rejectConfirmBtn: {
    padding: '0.45rem 1rem',
    backgroundColor: '#dc2626',
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.82rem',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.45rem 1rem',
    backgroundColor: '#e5e7eb',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontWeight: 600,
    fontSize: '0.82rem',
    cursor: 'pointer',
  },
};
